"use client";

import { useUserChoices } from "@/state/globalStore";
import {
  DeleteSituationsRequest,
  MindstringPrototype,
} from "@/generated/fastapi";
import type { ConfirmationDialogController } from "@/shared/ui/useConfirmationDialog";
import { showSuccessToast, showWarningToast } from "@/shared/feedback";
import { actRevalidatePrivateLayout } from "@/app/private-shell";
import { useDeleteSituationsMutation } from "@/features/situations";
import { useThoughtRefresh } from "@/features/thoughts";
import type { ActiveStudioContentType } from "@/shared/types";
import { PROFILE_SHAPING_MESSAGES } from "@/features/profile-shaping/config/actions";
import {
  type RelatedThoughtsDeleteChoice,
  useStudioDeletionStateContext,
} from "../contexts/StudioDeletionStateProvider";

type SituationSectionSelectionInput = {
  prototype: MindstringPrototype;
  situationId: string;
  activeStudioContent: ActiveStudioContentType;
  visibleThoughtIds: string[];
  isSectionSelected: boolean;
};

type DeleteSituationsOptions = {
  onSuccess?: () => void;
  skipConfirmation?: boolean;
};

export default function useStudioSituationActionHandlers({
  confirmSituationsDeletion,
}: {
  confirmSituationsDeletion?: ConfirmationDialogController<RelatedThoughtsDeleteChoice>; // TODO optional param? See how I solved it in useHandleSelectedThought
}) {
  const {
    addSelectedSituation,
    addSelectedSuggestedThoughts,
    selectedSituations,
    selectedThoughts,
    selectedThoughtsFree,
    removeSelectedSituation,
    removeSelectedSuggestedThoughts,
    selectThought,
    toggleSelectedSituation,
    unselectThoughts,
    setInterimState,
    removeSelectedSituations,
    resetSelectedThoughts,
    resetSelectedSituations,
    resetSelectedOrigins,
    resetSelectedSuggestedThoughts,
  } = useUserChoices();
  const { setPendingSituationDeletionRequest } =
    useStudioDeletionStateContext();
  const deleteSituations = useDeleteSituationsMutation();
  const { refreshStudioThoughts, refreshSuggestedThoughts } =
    useThoughtRefresh();

  async function handleSituationOnlySelectionClick(
    prototype: MindstringPrototype,
    situationId: string,
  ) {
    toggleSelectedSituation(prototype, situationId);
  }

  async function handleSituationSectionSelectionClick({
    prototype,
    situationId,
    activeStudioContent,
    visibleThoughtIds,
    isSectionSelected,
  }: SituationSectionSelectionInput) {
    if (isSectionSelected) {
      removeSelectedSituation(prototype, situationId);

      if (activeStudioContent === "suggested") {
        removeSelectedSuggestedThoughts(visibleThoughtIds);
        return;
      }

      unselectThoughts(visibleThoughtIds);
      return;
    }

    addSelectedSituation(prototype, situationId);

    if (activeStudioContent === "suggested") {
      addSelectedSuggestedThoughts(visibleThoughtIds);
      return;
    }

    visibleThoughtIds.forEach((thoughtId) => {
      selectThought({
        prototypeCategory: prototype,
        situationId,
        thoughtId,
      });
    });
  }

  async function handleSituationsDeleteClick(
    request: DeleteSituationsRequest,
    options?: DeleteSituationsOptions,
  ) {
    async function exectDeletion(
      deleteRequest: DeleteSituationsRequest,
      relatedThoughtsChoice?: RelatedThoughtsDeleteChoice,
    ) {
      // Keep old unassign_thoughts callers working while preferring the action enum.
      const finalRequest: DeleteSituationsRequest = {
        ...deleteRequest,
        related_thoughts_action:
          relatedThoughtsChoice?.action ??
          deleteRequest.related_thoughts_action ??
          (deleteRequest.unassign_thoughts ? "remove_anchor" : "delete"),
        anchor_thoughts_to_situation_id:
          relatedThoughtsChoice?.targetSituationId ??
          deleteRequest.anchor_thoughts_to_situation_id ??
          null,
      };

      try {
        const response = await deleteSituations.mutateAsync(finalRequest);

        if (response) {
          const situationCount = finalRequest.situation_ids.length;
          const relatedThoughtsAction = finalRequest.related_thoughts_action;
          const didRemoveRelatedThoughtAnchors =
            relatedThoughtsAction === "remove_anchor";
          const didAnchorRelatedThoughtsElsewhere =
            relatedThoughtsAction === "anchor_to_situation";

          if (response.count_active_situations === 0) {
            resetSelectedSituations();
            resetSelectedThoughts();
            resetSelectedOrigins();
            resetSelectedSuggestedThoughts();
            await actRevalidatePrivateLayout();
            showWarningToast(
              didRemoveRelatedThoughtAnchors
                ? "All situations have been deleted. Confirmed thoughts kept their profile place with anchors removed."
                : "All situations have been deleted. Studio is empty.",
            );
            options?.onSuccess?.();
            return;
          }
          removeSelectedSituations(finalRequest.situation_ids);
          refreshStudioThoughts();
          refreshSuggestedThoughts();
          showSuccessToast(
            didAnchorRelatedThoughtsElsewhere
              ? "Situation deleted. Related confirmed thoughts anchored to another situation."
              : didRemoveRelatedThoughtAnchors
                ? situationCount === 1
                  ? PROFILE_SHAPING_MESSAGES.situationDeletedThoughtsNeedAnchor
                  : PROFILE_SHAPING_MESSAGES.situationsDeletedThoughtsNeedAnchor
              : `${situationCount} situation${
                  situationCount === 1 ? "" : "s"
                } deleted`,
          );
          options?.onSuccess?.();
        } else {
          showWarningToast("Failed to delete situation");
        }
      } catch {
        showWarningToast("Failed to delete situation");
      }
    }

    if (confirmSituationsDeletion && !options?.skipConfirmation) {
      setPendingSituationDeletionRequest(request);
      confirmSituationsDeletion.onOpen((relatedThoughtsChoice) =>
        exectDeletion(request, relatedThoughtsChoice),
      );
    } else {
      await exectDeletion(request);
    }
  }

  async function handleSituationAddThoughtsClick(
    prototype: MindstringPrototype,
    situationId: string,
  ) {
    setInterimState("add_thoughts", {
      isOpen: true,
      value: situationId,
      prototypeCategory: prototype,
    });
  }

  async function handleSituationExtractThoughtsClick(
    prototype: MindstringPrototype,
    situationId: string,
  ) {
    setInterimState("extract_thoughts", {
      isOpen: true,
      value: situationId,
      prototypeCategory: prototype,
    });
  }

  async function handleSituationDetectBiasClick(
    prototype: MindstringPrototype,
    situationId: string,
  ) {
    setInterimState("insight_bias", {
      isOpen: true,
      value: situationId,
      prototypeCategory: prototype,
    });
  }

  async function handleSelectedSituationsUnselectClick(
    prototype: MindstringPrototype,
  ) {
    const selectedSituationIds =
      selectedSituations.find((group) => group.prototype === prototype)
        ?.situations_ids ?? [];
    const selectedThoughtIds = selectedThoughts
      .filter((selection) => selection.prototype === prototype)
      .flatMap((selection) => selection.thoughts_ids);
    const selectedFreeThoughtIds = selectedThoughtsFree
      .filter((selection) => selection.prototype === prototype)
      .flatMap((selection) => selection.thoughts_ids);

    unselectThoughts([...selectedThoughtIds, ...selectedFreeThoughtIds]);
    selectedSituationIds.forEach((situationId) => {
      removeSelectedSituation(prototype, situationId);
    });
  }

  return {
    handleSituationOnlySelectionClick,
    handleSituationSectionSelectionClick,
    handleSituationsDeleteClick,
    handleSituationAddThoughtsClick,
    handleSituationExtractThoughtsClick,
    handleSituationDetectBiasClick,
    handleSelectedSituationsUnselectClick,
  };
}
